import JsonStorage from '../../lib/localstorage';
import { getClosest } from '../../lib/lib';
import PicklistBanner from './picklist-banner';
import PicklistPage from './picklist-page';

const storageKey = 'PicklistKey';
const buttonSelector = '.c-picklist-button';

/**
 * Defines a picklist item (jsdoc yaaaay....)
 * @typedef {object} PicklistItem
 * @property {string} id Product id
 * @property {string} name Product name
 * @property {string} articleNumber Product article number
 * @property {string} url Link to product page
 * @property {string} thumbnail Image thumbnail source
 * @property {string} label1
 * @property {string} label2
 */

/**
 * Extract a picklist item from element dataset
 * @param {HtmlElement} element
 * @returns {PicklistItem}
 */
const extractData = element => {
  const {
    articleNumber,
    id,
    name,
    url,
    thumbnail,
    label1,
    label2,
  } = element.dataset;
  return {
    id,
    articleNumber,
    name,
    url,
    thumbnail,
    label1,
    label2,
  };
};

/**
 *
 * @param {PicklistItem} item
 * @returns {function(PicklistItem): boolean}
 */
const comparer = item => i => i.id === item.id;

const PickList = bannerElement => {
  const storage = JsonStorage(storageKey);
  let picklistItems = storage.get(storageKey) || [];
  const save = () => storage.set(picklistItems);
  const setCookie = () =>
    (document.cookie = `picklist-ids=${JSON.stringify(
      picklistItems.map(x => x.id),
    )};path=/`);
  const clear = () => {
    picklistItems = [];
    update();
  };
  const addItem = item => {
    if (!picklistItems.find(comparer(item))) picklistItems.push(item);
    update();
  };
  const removeItem = item => {
    picklistItems = picklistItems.filter(i => !comparer(item)(i));
    update();
  };
  const toggleItem = item => {
    const existing = picklistItems.find(comparer(item));
    if (existing) {
      picklistItems = picklistItems.filter(i => !comparer(item)(i));
    } else {
      picklistItems.push(item);
    }
    update();
  };
  const banner = PicklistBanner(bannerElement, picklistItems);
  const page = PicklistPage(picklistItems, removeItem, clear);
  setCookie();

  const update = () => {
    banner.update(picklistItems);
    page.update(picklistItems);
    setCookie();
    save();
  };

  return {
    addItem,
    removeItem,
    toggleItem,
    items: () => [...picklistItems],
    clear,
  };
};

export default function init() {
  const picklistBanner = document.querySelector('.c-picklist-banner');
  if (!picklistBanner) return;
  const picklist = PickList(picklistBanner);
  const buttons = [...document.querySelectorAll(buttonSelector)];

  const updateButton = button => {
    const included = picklist.items().find(comparer(extractData(button)));
    if (included) {
      button.classList.add('c-picklist-button--selected');
      button.setAttribute(
        'title',
        button.querySelector('.c-picklist-button-remove').textContent,
      );
    } else {
      button.classList.remove('c-picklist-button--selected');
      button.setAttribute(
        'title',
        button.querySelector('.c-picklist-button-add').textContent,
      );
    }
  };
  const buttonListener = event => {
    event.stopPropagation();
    const target = getClosest(event.target, buttonSelector);
    if (!target) return;
    const item = extractData(target);
    picklist.toggleItem(item);
    updateButton(target);
  };

  const setupButton = element => {
    element.addEventListener('click', buttonListener);
    updateButton(element);
  };

  buttons.forEach(setupButton);
  const mutationObserver = new MutationObserver(() => {
    document.querySelectorAll(buttonSelector).forEach(element => {
      if (Array.prototype.indexOf.call(buttons, element) < 0) {
        buttons.push(element);
        setupButton(element);
      }
    });
  });

  // attach mutation observer to any places where buttons may appear later on
  document
    .querySelectorAll('.winelistingpage')
    .forEach(element =>
      mutationObserver.observe(element, { subtree: true, childList: true }),
    );
}
